/* Progressive Enhancement: ohne JS bleiben alle Inhalte und Links verfügbar. */
(() => {
  'use strict';
  if (customElements.get('gr-druckdaten')) return;

  class GrDruckdaten extends HTMLElement {
    connectedCallback() {
      if (this._controller) return;
      this._controller = new AbortController();
      const options = { signal: this._controller.signal };
      this.tabs = [...this.querySelectorAll('[data-tab]')];
      this.panels = [...this.querySelectorAll('[data-panel]')];
      this.tablist = this.querySelector('[data-tabs]');
      this.allButton = this.querySelector('[data-show-all]');
      if (!this.tabs.length || !this.panels.length) return;
      this.allButton.hidden = false;
      this.addEventListener('click', event => this.onClick(event), options);
      this.tablist.addEventListener('keydown', event => this.onKeydown(event), options);
      this.addEventListener('input', event => {
        if (event.target.matches('[data-pixels], [data-cm]')) this.calculate();
      }, options);
      window.addEventListener('hashchange', () => this.route(false), options);
      window.addEventListener('popstate', () => this.route(false), options);
      window.addEventListener('beforeprint', () => {
        this._closedBeforePrint = [...this.querySelectorAll('details:not([open])')];
        this._closedBeforePrint.forEach(item => { item.open = true; });
      }, options);
      window.addEventListener('afterprint', () => {
        (this._closedBeforePrint || []).forEach(item => { item.open = false; });
        this._closedBeforePrint = [];
      }, options);
      document.addEventListener('shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        const panel = event.target.closest('[data-panel]');
        if (panel) this.select(panel.id);
        const detail = event.target.closest('details');
        if (detail) detail.open = true;
      }, options);
      this.route(Boolean(location.hash));
      this.calculate();
    }

    disconnectedCallback() {
      this._controller?.abort();
      this._controller = null;
      clearTimeout(this._announceTimer);
      cancelAnimationFrame(this._frame);
    }

    select(id, focusTab = false) {
      if (!this.panels.some(panel => panel.id === id)) id = this.dataset.defaultPanel;
      this.selectedId = id;
      this.showingAll = false;
      this.tablist.setAttribute('role', 'tablist');
      this.tabs.forEach(tab => {
        const selected = tab.dataset.tab === id;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', tab.dataset.tab);
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      this.panels.forEach(panel => {
        panel.hidden = panel.id !== id;
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', `grdd-tab-${panel.id}`);
      });
      this.allButton.textContent = 'Alle Vorgaben anzeigen ↗';
      this.allButton.setAttribute('aria-expanded', 'false');
      if (focusTab) this.tabs.find(tab => tab.dataset.tab === id)?.focus();
    }

    showAll() {
      this.showingAll = true;
      this.tablist.removeAttribute('role');
      this.tabs.forEach(tab => {
        ['role', 'aria-controls', 'aria-selected', 'tabindex'].forEach(name => tab.removeAttribute(name));
      });
      this.panels.forEach(panel => {
        panel.hidden = false;
        panel.removeAttribute('role');
        panel.setAttribute('aria-labelledby', `grdd-title-${panel.id}`);
      });
      this.allButton.textContent = 'Zur Einzelauswahl zurück ↗';
      this.allButton.setAttribute('aria-expanded', 'true');
    }

    getHash() {
      try { return decodeURIComponent(location.hash.slice(1)); }
      catch (_) { return ''; }
    }

    route(scroll) {
      const id = this.getHash();
      if (id === 'alle-vorgaben') this.showAll();
      else {
        const target = document.getElementById(id);
        const panel = target && this.contains(target) ? target.closest('[data-panel]') : null;
        this.select(panel?.id || this.selectedId || this.dataset.defaultPanel);
        if (!id) this.select(this.dataset.defaultPanel);
        if (target && this.contains(target)) this.openParents(target);
      }
      if (scroll && id) this.scrollToId(id, false);
    }

    setHash(id) {
      const hash = `#${encodeURIComponent(id)}`;
      if (location.hash === hash) return;
      try { history.pushState(null, '', hash); }
      catch (_) { location.hash = hash; }
    }

    openParents(target) {
      let current = target;
      while (current && current !== this) {
        if (current.tagName === 'DETAILS') current.open = true;
        current = current.parentElement;
      }
    }

    scrollToId(id, focus = true) {
      const target = document.getElementById(id);
      if (!target || !this.contains(target)) return;
      cancelAnimationFrame(this._frame);
      this._frame = requestAnimationFrame(() => {
        this.openParents(target);
        target.scrollIntoView({ block: 'start', behavior: 'auto' });
        if (focus) {
          const focusTarget = target.tagName === 'DETAILS' ? target.querySelector('summary') : target;
          if (!focusTarget.hasAttribute('tabindex') && !focusTarget.matches('a,button,input,summary')) focusTarget.setAttribute('tabindex', '-1');
          focusTarget.focus({ preventScroll: true });
        }
      });
    }

    async onClick(event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const all = event.target.closest('[data-show-all]');
      if (all) {
        const next = this.showingAll ? (this.selectedId || this.dataset.defaultPanel) : 'alle-vorgaben';
        this.setHash(next);
        this.route(false);
        this.announce(this.showingAll ? 'Alle Vorgaben sind sichtbar.' : 'Einzelauswahl ist aktiv.');
        return;
      }
      const copyLink = event.target.closest('[data-copy-link]');
      if (copyLink) {
        event.preventDefault();
        const url = new URL(location.href);
        url.hash = copyLink.dataset.copyLink;
        try {
          if (url.protocol === 'file:') throw new Error('Local preview');
          await navigator.clipboard.writeText(url.href);
          this.announce('Link zum Verfahren kopiert.');
          const original = copyLink.textContent;
          copyLink.textContent = 'Link kopiert ✓';
          setTimeout(() => { if (copyLink.isConnected) copyLink.textContent = original; }, 2300);
        } catch (_) {
          this.setHash(copyLink.dataset.copyLink);
          this.announce(url.protocol === 'file:' ? 'In der lokalen Vorschau gibt es noch keinen öffentlichen Seitenlink.' : 'Der Link steht jetzt in der Adresszeile. Bitte kopiere ihn dort.');
        }
        return;
      }
      const link = event.target.closest('a[href^="#"]');
      if (!link || !this.contains(link)) return;
      const id = link.getAttribute('href').slice(1);
      const target = document.getElementById(id);
      if (!target || !this.contains(target)) return;
      event.preventDefault();
      const panel = target.closest('[data-panel]');
      if (panel) this.select(panel.id);
      if (id === 'alle-vorgaben') this.showAll();
      this.setHash(id);
      if (link.hasAttribute('data-tab')) {
        link.focus({ preventScroll: true });
      } else this.scrollToId(id);
    }

    onKeydown(event) {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      const tab = event.target.closest('[data-tab]');
      if (!tab || this.showingAll) return;
      const index = this.tabs.indexOf(tab);
      let next;
      if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.tabs.length - 1;
      if (event.key === ' ') next = index;
      if (next === undefined) return;
      event.preventDefault();
      const id = this.tabs[next].dataset.tab;
      this.select(id, true);
      this.setHash(id);
    }

    announce(message) {
      const status = this.querySelector('[data-announcer]');
      status.textContent = '';
      clearTimeout(this._announceTimer);
      this._announceTimer = setTimeout(() => { status.textContent = message; }, 30);
    }

    calculate() {
      const pixelsField = this.querySelector('[data-pixels]');
      const cmField = this.querySelector('[data-cm]');
      const result = this.querySelector('[data-calc-result]');
      const pixelRaw = pixelsField.value.trim();
      const cmRaw = cmField.value.trim();
      const pixels = /^\d+$/.test(pixelRaw) ? Number(pixelRaw) : NaN;
      const cm = /^\d+(?:[,.]\d+)?$/.test(cmRaw) ? Number(cmRaw.replace(',', '.')) : NaN;
      const pixelValid = Number.isSafeInteger(pixels) && pixels > 0 && pixels <= 1000000;
      const cmValid = Number.isFinite(cm) && cm >= 0.1 && cm <= 1000;
      pixelsField.setAttribute('aria-invalid', String(!pixelValid));
      cmField.setAttribute('aria-invalid', String(!cmValid));
      if (!pixelValid || !cmValid) {
        result.replaceChildren();
        const message = document.createElement('p');
        message.textContent = !pixelValid ? 'Bitte eine ganze Pixelzahl von 1 bis 1000000 ohne Tausendertrennzeichen eingeben.' : 'Bitte eine Druckbreite von 0,1 bis 1000 cm eingeben, zum Beispiel 25,4.';
        result.append(message);
        return;
      }
      const target = Number(this.dataset.targetPpi) || 300;
      const effective = pixels * 2.54 / cm;
      const maxCm = pixels * 2.54 / target;
      // Epsilon vermeidet +1 Pixel durch reine Gleitkomma-Rundung bei exakten Zollmaßen.
      const required = Math.ceil(cm / 2.54 * target - 1e-9);
      const formatted = number => new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(number);
      const meetsTarget = effective + 1e-9 >= target;
      const line = document.createElement('p');
      const value = document.createElement('strong');
      value.textContent = !meetsTarget && Number(effective.toFixed(1)) >= target ? `Knapp unter ${target} dpi` : `${formatted(effective)} dpi`;
      line.append(value, document.createTextNode(`bei ${formatted(cm)} cm Druckbreite`));
      const advice = document.createElement('p');
      advice.textContent = meetsTarget ? `Die Pixelzahl erreicht unseren Zielwert von ${target} dpi. Prüfe zusätzlich Schärfe, Hintergrund und Details.` : `Unter unserem Zielwert von ${target} dpi. Für diese Breite brauchst du mindestens ${formatted(required)} Pixel. Frag möglichst nach der größeren Originaldatei.`;
      const limit = document.createElement('p');
      limit.textContent = `Bei ${target} dpi reichen deine ${formatted(pixels)} Pixel für etwa ${formatted(maxCm)} cm Breite. Das ist keine Freigabe für das Motiv.`;
      result.replaceChildren(line, advice, limit);
    }
  }
  customElements.define('gr-druckdaten', GrDruckdaten);
})();
